import { Paper, Stack, Text, Button, ThemeIcon, Title } from '@mantine/core';
import { Link } from 'react-router-dom';
import type { ComponentType, ReactNode } from 'react';
import type { TablerIcon } from '@tabler/icons-react';

interface EmptyStateAction {
  label: string;
  /** Navigates to this route. Takes precedence; `onClick` is ignored when both are set. */
  to?: string;
  /** Click handler. Ignored when `to` is also set. */
  onClick?: () => void;
}

interface EmptyStateProps {
  icon: TablerIcon | ComponentType<{ size?: number }>;
  title: string;
  description?: ReactNode;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  /** 'card' (default): bordered Paper. 'inline': bare Stack for embedding in existing Papers/charts. */
  variant?: 'card' | 'inline';
  /** Extra content below actions, e.g. an inline tank picker. */
  children?: ReactNode;
  'data-testid'?: string;
}

function ActionButton({ action, primary }: { action: EmptyStateAction; primary: boolean }) {
  const shared = primary
    ? { size: 'md' as const }
    : { variant: 'subtle' as const, size: 'sm' as const };
  if (action.to) {
    return (
      <Button component={Link} to={action.to} {...shared}>
        {action.label}
      </Button>
    );
  }
  return (
    <Button onClick={action.onClick} {...shared}>
      {action.label}
    </Button>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  variant = 'card',
  children,
  'data-testid': dataTestId = 'empty-state',
}: EmptyStateProps) {
  const body = (
    <Stack align="center" gap="md">
      <ThemeIcon size={64} radius="xl" variant="light" data-testid={`${dataTestId}-icon`}>
        <Icon size={32} />
      </ThemeIcon>
      <Title order={3} size="lg" fw={500}>
        {title}
      </Title>
      {description && (
        <Text c="dimmed" size="sm" maw={300}>
          {description}
        </Text>
      )}
      {primaryAction && <ActionButton action={primaryAction} primary />}
      {secondaryAction && <ActionButton action={secondaryAction} primary={false} />}
      {children}
    </Stack>
  );

  if (variant === 'inline') {
    return (
      <Stack align="center" py="lg" data-testid={dataTestId}>
        {body}
      </Stack>
    );
  }
  return (
    <Paper p="xl" withBorder style={{ textAlign: 'center' }} data-testid={dataTestId}>
      {body}
    </Paper>
  );
}
